import type { CardField, FieldModes, FlashcardConfig, FlashcardPreset, Language } from './types';

/** Backend route builders (paths only — prefix with the API base URL). */
export const API_ROUTES = {
  // Auth — the only routes reachable without a token
  authRegister: '/auth/register',
  authLogin: '/auth/login',
  authRefresh: '/auth/refresh',
  authLogout: '/auth/logout',

  // Catalog (auth) — the course content, served from the bundle in the Lambda
  languages: '/languages',
  language: (code: string) => `/languages/${encodeURIComponent(code)}`,
  book: (bookId: string) => `/books/${encodeURIComponent(bookId)}`,
  chapter: (chapterId: string) => `/chapters/${encodeURIComponent(chapterId)}`,
  section: (sectionId: string) => `/sections/${encodeURIComponent(sectionId)}`,
  /** One deck assembled from several sections, for a custom session. */
  deck: (sectionIds: string[]) =>
    `/study/deck?sectionIds=${encodeURIComponent(sectionIds.join(','))}`,

  // Progress (auth)
  bookProgress: (bookId: string) => `/progress/books/${encodeURIComponent(bookId)}`,
  recordSession: (sectionId: string) =>
    `/progress/sections/${encodeURIComponent(sectionId)}/session`,
  markStudied: (sectionId: string) =>
    `/progress/sections/${encodeURIComponent(sectionId)}/studied`,
  stats: '/progress/stats',
  /** Cards whose review interval has elapsed, most overdue first. */
  due: (languageCode: string, limit?: number) =>
    `/progress/due?languageCode=${encodeURIComponent(languageCode)}` +
    (limit ? `&limit=${limit}` : ''),
  /** A finished review or custom session, whose cards may span sections. */
  recordReviewSession: '/progress/session',

  // Settings (auth)
  settings: '/settings',
} as const;

// ── Flashcard configuration ───────────────────────────────────────────────

/**
 * Recognition is the default because it is the only mode that works on a card
 * you have never seen: you are shown the word and asked whether you know it,
 * rather than asked to produce something you were never taught.
 */
export const DEFAULT_FLASHCARD_CONFIG: FlashcardConfig = {
  fields: {
    target: 'prompt',
    translit: 'answer',
    translation: 'answer',
    audio: 'answer',
  },
  autoPlayAudio: false,
  shuffle: true,
  repeatMissed: true,
  speechRate: 0.9,
};

export const FLASHCARD_PRESETS: FlashcardPreset[] = [
  {
    id: 'recognition',
    name: 'Recognition',
    description: 'See the word, recall what it means. The gentlest way into new vocabulary.',
    config: {
      fields: { target: 'prompt', translit: 'answer', translation: 'answer', audio: 'answer' },
    },
  },
  {
    id: 'recall',
    name: 'Production',
    description: 'See the English, produce the word yourself. Much harder, and what speaking asks of you.',
    config: {
      fields: { target: 'answer', translit: 'answer', translation: 'prompt', audio: 'answer' },
    },
  },
  {
    id: 'listening',
    name: 'Listening',
    description: 'Hear it and nothing else — no spelling to lean on. Trains the ear the way conversation does.',
    config: {
      fields: { target: 'answer', translit: 'answer', translation: 'answer', audio: 'prompt' },
    },
  },
  {
    id: 'dictation',
    name: 'Dictation',
    description: 'Hear the word and recall how it is written. The bridge from sound to spelling.',
    config: {
      fields: { target: 'answer', translit: 'hidden', translation: 'answer', audio: 'prompt' },
    },
  },
  {
    id: 'reading-script',
    name: 'Reading the script',
    description:
      'See the written form, recall its pronunciation and meaning. The mode that makes characters stick.',
    requiresTransliteration: true,
    config: {
      fields: { target: 'prompt', translit: 'answer', translation: 'answer', audio: 'answer' },
    },
  },
  {
    id: 'script-from-sound',
    name: 'Script from sound',
    description:
      'Given the romanization and the meaning, recall the written form. The hardest direction, and the one that lasts.',
    requiresTransliteration: true,
    config: {
      fields: { target: 'answer', translit: 'prompt', translation: 'prompt', audio: 'answer' },
    },
  },
];

/**
 * Offered sitting lengths, in cards. `0` means the whole deck in one go.
 *
 * A section here runs from 8 cards to 37, and a chapter's vocabulary can reach
 * the high hundreds, so "all of it" and "a short sitting" both have to be one
 * click away. Anything at or above the deck size is dropped as a choice, since
 * it would behave identically to `All`.
 */
export const SITTING_SIZES = [10, 20, 30, 50] as const;

export interface SittingChoice {
  /** Cards per sitting, or 0 for the whole deck. */
  value: number;
  label: string;
  /** How many sittings this cuts the deck into. */
  sittings: number;
}

export function sittingChoices(deckSize: number): SittingChoice[] {
  // A deck size of 0 means "not known here" — the profile page configures the
  // default without any one section in mind. Offer every size and no counts.
  const known = deckSize > 0;
  const sizes = known ? SITTING_SIZES.filter((size) => size < deckSize) : [...SITTING_SIZES];
  const choices: SittingChoice[] = sizes.map((size) => ({
    value: size,
    label: String(size),
    sittings: known ? Math.ceil(deckSize / size) : 1,
  }));
  choices.push({ value: 0, label: known ? `All ${deckSize}` : 'All', sittings: 1 });
  return choices;
}

/** The choice a stored size corresponds to, treating anything >= the deck as All. */
export function activeSittingValue(sessionSize: number, deckSize: number): number {
  if (sessionSize <= 0) return 0;
  if (deckSize > 0 && sessionSize >= deckSize) return 0;
  return sessionSize;
}

/** Fields a language actually has. Spanish has no transliteration, so no `translit`. */
export function availableFields(language: Pick<Language, 'transliteration'>): CardField[] {
  const fields: CardField[] = ['target', 'translation', 'audio'];
  if (language.transliteration) fields.splice(1, 0, 'translit');
  return fields;
}

/**
 * Why a field configuration cannot be used, or null if it is fine.
 *
 * A card needs something to ask (`prompt`) and something to reveal (`answer`).
 * Without the first there is no question; without the second, flipping the card
 * shows you what you were already looking at. `translit` is excluded from the
 * check for languages that have none, so Spanish is not held to a rule about a
 * field it does not possess.
 */
export function validateFieldModes(
  fields: FieldModes,
  language: Pick<Language, 'transliteration'>,
): string | null {
  const usable = availableFields(language);
  const modes = usable.map((f) => fields[f]);

  if (!modes.includes('prompt')) {
    return 'Show at least one field on the front — otherwise there is no question to answer.';
  }
  if (!modes.includes('answer')) {
    return 'Reveal at least one field on the back — otherwise flipping the card shows nothing new.';
  }
  return null;
}

/** The preset whose field modes match this configuration, or null for a custom one. */
export function matchingPreset(
  fields: FieldModes,
  language: Pick<Language, 'transliteration'>,
): FlashcardPreset | null {
  const usable = availableFields(language);
  return (
    FLASHCARD_PRESETS.find((preset) => {
      if (preset.requiresTransliteration && !language.transliteration) return false;
      return usable.every((field) => preset.config.fields[field] === fields[field]);
    }) ?? null
  );
}

/** Presets offered for a language — transliteration modes are hidden without one. */
export function presetsFor(language: Pick<Language, 'transliteration'>): FlashcardPreset[] {
  return FLASHCARD_PRESETS.filter(
    (preset) => !preset.requiresTransliteration || language.transliteration,
  );
}
